import type { FrameTime, MediaState, PropertyDescriptor, SourceContext, SourceTypeDefinition } from '../core/definitions';
import { BaseSourceRuntime, bool, num, str } from './base';
import { hasRef, probeVideoCodec, resolveUrl, validateMediaUrl, type MediaRef } from './assetLoad';
import { mapPlayFailure, toMapped } from './errors';
import { ElementFrameObserver, FrameCounter, type FrameStats } from './frameStats';
import { PAUSE_RETRY_WINDOW_MS } from './liveness';

export type MediaSettings = {
  assetId: string;
  url: string;
  loop: boolean;
  autoplay: boolean;
  restartOnActivate: boolean;
  /** Pause while not on program and resume where it was when it returns. */
  pauseWhenHidden: boolean;
  /** In and out points in ms; endMs 0 plays to the end of the file. */
  startMs: number;
  endMs: number;
  volume: number;
  playbackRate: number;
  muted: boolean;
};

export const MEDIA_DEFAULTS: MediaSettings = {
  assetId: '',
  url: '',
  loop: false,
  autoplay: true,
  restartOnActivate: false,
  pauseWhenHidden: false,
  startMs: 0,
  endMs: 0,
  volume: 1,
  playbackRate: 1,
  muted: false,
};

function read(s: Partial<MediaSettings>): MediaSettings {
  const d = MEDIA_DEFAULTS;
  return {
    assetId: str(s.assetId, d.assetId),
    url: str(s.url, d.url),
    loop: bool(s.loop, d.loop),
    autoplay: bool(s.autoplay, d.autoplay),
    restartOnActivate: bool(s.restartOnActivate, d.restartOnActivate),
    pauseWhenHidden: bool(s.pauseWhenHidden, d.pauseWhenHidden),
    startMs: Math.round(num(s.startMs, d.startMs, 0, 86_400_000)),
    endMs: Math.round(num(s.endMs, d.endMs, 0, 86_400_000)),
    volume: num(s.volume, d.volume, 0, 2),
    playbackRate: num(s.playbackRate, d.playbackRate, 0.0625, 16),
    muted: bool(s.muted, d.muted),
  };
}

export interface Transport {
  currentTime: number;
  duration: number;
  paused: boolean;
}

/** Buffering shorter than this is not worth a status change (seeks, brief network dips). */
export const BUFFERING_NOTICE_MS = 400;
/** A load or buffering stall that lasts this long becomes an error. */
export const MEDIA_STALL_TIMEOUT_MS = 15_000;

/** How far before the out point the held frame is taken when playback overshot it (a little over one 30 fps frame). */
const OUT_POINT_HOLD_MS = 40;

/** Element events after which getMediaState() reads differently; timeupdate (about 4 Hz) keeps the position fresh. */
const MEDIA_STATE_EVENTS = ['play', 'pause', 'seeked', 'durationchange', 'ratechange', 'timeupdate'] as const;

const ERROR_NAMES: Record<number, string> = {
  1: 'Playback was aborted',
  2: 'A network error interrupted loading',
  3: 'The file could not be decoded',
  4: 'The file format is not supported',
};

export class MediaRuntime extends BaseSourceRuntime<MediaSettings> {
  private cfg: MediaSettings;
  private readonly el = document.createElement('video');
  // Volume and mute live on a gain node: the element itself must stay unmuted or the node would carry silence.
  private readonly gain: GainNode;
  private readonly node: MediaElementAudioSourceNode;
  // Chrome only advances an element routed into Web Audio while that graph is being pulled, and an
  // unconnected output is not. An analyser is pulled automatically, so playback never depends on downstream wiring.
  private readonly pull: AnalyserNode;
  private active = false;
  private ref: MediaRef;
  private readonly counter = new FrameCounter();
  private readonly frameObserver: ElementFrameObserver;
  private intentionalPause = true;
  private lastPauseAt = -Infinity;
  private pauseRetried = false;
  private bufferTimer: ReturnType<typeof setTimeout> | null = null;
  private stallTimer: ReturnType<typeof setTimeout> | null = null;
  /** After `stop` until the next play: reported as 'stopped' rather than 'paused'. */
  private stopped = false;
  /** Playback reached `endMs` without looping; the element itself has not ended. */
  private outReached = false;
  /** Paused by pauseWhenHidden while it was playing, so returning to program resumes it. */
  private resumeOnActive = false;
  /** Video codec named by the container: undefined until probed, null for audio-only or unknown. */
  private videoCodec: string | null | undefined;

  constructor(ctx: SourceContext, settings: Partial<MediaSettings>) {
    super(ctx);
    this.cfg = read(settings);
    this.ref = { assetId: this.cfg.assetId, url: this.cfg.url };
    const el = this.el;
    el.playsInline = true;
    el.preload = 'auto';
    this.gain = ctx.audioContext.createGain();
    this.node = ctx.audioContext.createMediaElementSource(el);
    this.node.connect(this.gain);
    this.pull = ctx.audioContext.createAnalyser();
    this.gain.connect(this.pull);
    this.setAudio(this.gain);
    for (const name of ['loadedmetadata', 'resize', 'emptied'] as const) el.addEventListener(name, this.syncSize);
    el.addEventListener('loadeddata', this.onLoaded);
    el.addEventListener('playing', this.onPlaying);
    el.addEventListener('seeked', this.onPlaying);
    el.addEventListener('ended', this.onEnded);
    el.addEventListener('error', this.onError);
    el.addEventListener('pause', this.onPause);
    el.addEventListener('waiting', this.onBuffering);
    el.addEventListener('stalled', this.onBuffering);
    el.addEventListener('canplay', this.onFlowing);
    el.addEventListener('timeupdate', this.checkOutPoint);
    for (const name of MEDIA_STATE_EVENTS) el.addEventListener(name, this.notify);
    this.frameObserver = new ElementFrameObserver(el, this.counter);
    this.frameObserver.start();
    this.applyPlayback();
    void this.load();
  }

  override getFrameStats(): FrameStats {
    this.frameObserver.poll();
    return this.counter.stats();
  }

  /** A playing clip delivers pictures at its frame rate; a paused, stopped, ended or audio-only one legitimately delivers none. */
  expectsContinuousFrames(): boolean {
    const el = this.el;
    return this.getStatus().state === 'live' && !el.paused && !el.ended && el.videoWidth > 0 && el.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA;
  }

  getTransport(): Transport {
    return { currentTime: this.el.currentTime, duration: Number.isFinite(this.el.duration) ? this.el.duration : 0, paused: this.el.paused };
  }

  getMediaState(): MediaState | null {
    if (!hasRef(this.ref) || this.getStatus().state === 'error' || this.getStatus().state === 'idle') return null;
    const el = this.el;
    const durationMs = Number.isFinite(el.duration) ? Math.round(el.duration * 1000) : 0;
    let state: MediaState['state'];
    if (this.stopped) state = 'stopped';
    else if (el.ended || this.outReached) state = 'ended';
    else if (this.getStatus().state === 'starting' && !el.paused) state = 'buffering';
    else state = el.paused ? 'paused' : 'playing';
    return { state, timeMs: Math.round(el.currentTime * 1000), durationMs };
  }

  update(settings: MediaSettings): void {
    if (this.destroyed) return;
    const prev = this.cfg;
    const next = read(settings);
    this.cfg = next;
    this.applyPlayback();
    if (next.assetId !== this.ref.assetId || next.url !== this.ref.url) {
      this.ref = { assetId: next.assetId, url: next.url };
      void this.load();
      return;
    }
    if (next.startMs !== prev.startMs || next.endMs !== prev.endMs) {
      this.outReached = false;
      const t = this.el.currentTime * 1000;
      if (t < next.startMs || (this.endMs() > 0 && t >= this.endMs())) this.seekMs(next.startMs);
    }
    // Turning autoplay on for a loaded, idle clip starts it, as it would have on load.
    if (!prev.autoplay && next.autoplay && this.getStatus().state === 'live' && this.el.paused && !this.el.ended && !this.stopped && this.mayAutoplay()) {
      this.play();
    }
  }

  tick(time: FrameTime): void {
    // Look one composed frame ahead so the frame at the out point itself is never shown.
    this.checkOutPoint(Math.min(100, time.deltaMs) * this.el.playbackRate);
  }

  setActive(active: boolean): void {
    if (this.destroyed || active === this.active) return;
    this.active = active;
    if (this.cfg.restartOnActivate) {
      if (active) {
        this.seekMs(this.cfg.startMs);
        this.outReached = false;
        if (this.cfg.autoplay) this.play();
      } else {
        this.pauseBy('hidden');
      }
      return;
    }
    if (!this.cfg.pauseWhenHidden) return;
    if (active) {
      if (this.resumeOnActive) this.play();
      this.resumeOnActive = false;
    } else if (!this.el.paused && !this.el.ended) {
      this.resumeOnActive = true;
      this.pauseBy('hidden');
    }
  }

  invoke(action: string, args?: unknown): void {
    if (this.destroyed) return;
    switch (action) {
      case 'play':
        this.play();
        break;
      case 'pause':
        this.resumeOnActive = false;
        this.pauseBy('user');
        break;
      case 'restart':
        this.seekMs(this.cfg.startMs);
        this.play();
        break;
      case 'stop':
        this.resumeOnActive = false;
        this.pauseBy('user');
        this.seekMs(this.cfg.startMs);
        this.stopped = true;
        if (this.getStatus().state === 'live' || this.getStatus().state === 'ended') this.setStatus('live', undefined, 'stopped');
        break;
      case 'seek': {
        // Milliseconds, like MediaState.timeMs.
        const t = typeof args === 'number' ? args : Number(args);
        if (Number.isFinite(t)) {
          this.outReached = false;
          this.seekMs(Math.max(0, t));
        }
        break;
      }
      case 'retry':
        void this.load();
        break;
    }
  }

  private endMs(): number {
    return this.cfg.endMs > this.cfg.startMs ? this.cfg.endMs : 0;
  }

  /** In or out points need our own looping: the element's native loop always restarts at 0. */
  private nativeLoop(): boolean {
    return this.cfg.loop && this.cfg.startMs === 0 && this.endMs() === 0;
  }

  private mayAutoplay(): boolean {
    return (!this.cfg.restartOnActivate && !this.cfg.pauseWhenHidden) || this.active;
  }

  private seekMs(ms: number): void {
    const duration = this.el.duration;
    const t = ms / 1000;
    this.el.currentTime = Number.isFinite(duration) && duration > 0 ? Math.min(t, duration) : t;
  }

  private pauseBy(reason: 'user' | 'hidden'): void {
    this.intentionalPause = true;
    this.el.pause();
    if (reason === 'user' && this.getStatus().state === 'live') this.setStatus('live', undefined, 'paused');
  }

  private play(): void {
    if (this.el.ended || this.outReached) this.seekMs(this.cfg.startMs);
    this.stopped = false;
    this.outReached = false;
    this.intentionalPause = false;
    this.el.play().catch((err) => {
      if (this.destroyed) return;
      this.log('warn', 'play() was rejected', err);
      const failure = mapPlayFailure(err);
      if (failure) this.setStatus(failure.state, failure.error);
    });
  }

  private applyPlayback(): void {
    this.el.loop = this.nativeLoop();
    this.el.defaultPlaybackRate = this.cfg.playbackRate;
    this.el.playbackRate = this.cfg.playbackRate;
    this.gain.gain.value = this.cfg.muted ? 0 : this.cfg.volume;
  }

  private readonly notify = (): void => {
    this.emit();
  };

  private readonly checkOutPoint = (aheadMs: unknown = 0): void => {
    const end = this.endMs();
    if (end === 0 || this.destroyed || this.el.paused || this.outReached) return;
    if (this.el.currentTime * 1000 + (typeof aheadMs === 'number' ? aheadMs : 0) < end) return;
    if (this.cfg.loop) {
      this.seekMs(this.cfg.startMs);
      return;
    }
    this.outReached = true;
    this.intentionalPause = true;
    this.el.pause();
    // The picture on screen can be a frame presented ahead of currentTime, or past the out point when a
    // busy main thread noticed it late. Seeking always replaces it with a frame from before the out point.
    this.seekMs(Math.max(this.cfg.startMs, Math.min(this.el.currentTime * 1000, end - OUT_POINT_HOLD_MS)));
    this.setStatus('ended');
  };

  private readonly syncSize = (): void => {
    const el = this.el;
    if (el.videoWidth > 0 && el.videoHeight > 0) {
      const cur = this.getVideo();
      if (!cur || cur.width !== el.videoWidth || cur.height !== el.videoHeight) this.publishVideo(el, el.videoWidth, el.videoHeight, true);
    } else {
      this.clearVideo();
    }
    this.checkDecodable();
  };

  /**
   * A container whose video track the browser cannot decode (HEVC without platform support, ProRes)
   * still loads and plays its sound with no picture. Report that instead of playing sound only.
   */
  private checkDecodable(): void {
    const codec = this.videoCodec;
    const el = this.el;
    if (!codec || this.destroyed || el.readyState < HTMLMediaElement.HAVE_METADATA || el.videoWidth > 0) return;
    this.unload();
    this.fail('unsupported', `This browser cannot decode the file's video (${codec.trim()}), so only its sound would play. Convert it to H.264 MP4 or VP9 WebM.`);
  }

  private readonly onLoaded = (): void => {
    this.clearStallTimers();
    this.syncSize();
    if (this.getStatus().state === 'error') return;
    if (this.cfg.startMs > 0 && this.el.currentTime * 1000 < this.cfg.startMs) this.seekMs(this.cfg.startMs);
    this.setStatus('live');
    if (!this.cfg.autoplay) return;
    if (this.mayAutoplay()) this.play();
    else if (this.cfg.pauseWhenHidden) this.resumeOnActive = true;
  };

  private readonly onPlaying = (): void => {
    this.clearStallTimers();
    if (this.el.ended || this.outReached) return;
    if (this.stopped) this.setStatus('live', undefined, 'stopped');
    else if (this.el.paused && this.intentionalPause) this.setStatus('live', undefined, this.getStatus().detail === 'paused' ? 'paused' : undefined);
    else this.setStatus('live');
  };

  // The browser (not us) paused the element, e.g. a background-tab optimisation: resume once, then report.
  private readonly onPause = (): void => {
    if (this.destroyed || this.intentionalPause || this.el.ended || this.getStatus().state !== 'live') return;
    const now = performance.now();
    const giveUp = () => this.fail('unknown', 'The browser paused the media and it could not be resumed');
    if (this.pauseRetried && now - this.lastPauseAt < PAUSE_RETRY_WINDOW_MS) {
      giveUp();
      return;
    }
    this.pauseRetried = true;
    this.lastPauseAt = now;
    this.el.play().catch(giveUp);
  };

  private readonly onBuffering = (): void => {
    if (this.destroyed || this.el.paused || this.el.ended || this.getStatus().state !== 'live' || this.bufferTimer !== null) return;
    this.bufferTimer = setTimeout(() => {
      this.bufferTimer = null;
      if (this.destroyed || this.el.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) return;
      this.setStatus('starting', { code: 'unknown', message: 'Buffering: the media is not delivering data' }, 'buffering');
      this.armStallTimer();
    }, BUFFERING_NOTICE_MS);
  };

  private readonly onFlowing = (): void => {
    this.clearStallTimers();
    const state = this.getStatus().state;
    if (!this.el.ended && (state === 'starting' || state === 'error') && this.el.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      this.setStatus('live');
    }
  };

  private armStallTimer(): void {
    if (this.stallTimer !== null) return;
    this.stallTimer = setTimeout(() => {
      this.stallTimer = null;
      if (!this.destroyed) this.fail('load-failed', `The media did not deliver data for ${MEDIA_STALL_TIMEOUT_MS / 1000} s`);
    }, MEDIA_STALL_TIMEOUT_MS);
  }

  private clearStallTimers(): void {
    if (this.bufferTimer !== null) clearTimeout(this.bufferTimer);
    if (this.stallTimer !== null) clearTimeout(this.stallTimer);
    this.bufferTimer = null;
    this.stallTimer = null;
  }

  private readonly onEnded = (): void => {
    this.clearStallTimers();
    if (this.cfg.loop && !this.nativeLoop()) {
      this.seekMs(this.cfg.startMs);
      this.play();
      return;
    }
    if (!this.cfg.loop) this.setStatus('ended');
  };

  private readonly onError = (): void => {
    this.clearStallTimers();
    const code = this.el.error?.code ?? 0;
    this.fail('load-failed', this.el.error?.message || ERROR_NAMES[code] || 'Media failed to load');
  };

  private unload(): void {
    this.clearStallTimers();
    this.pauseRetried = false;
    this.intentionalPause = true;
    this.stopped = false;
    this.outReached = false;
    this.el.pause();
    this.el.removeAttribute('src');
    this.el.load();
    this.clearVideo();
  }

  private async load(): Promise<void> {
    const epoch = this.beginEpoch();
    this.unload();
    this.videoCodec = undefined;
    if (!hasRef(this.ref)) {
      this.setStatus('idle');
      return;
    }
    this.setStatus('starting');
    if (this.ref.assetId) void this.probe(this.ref.assetId, epoch);
    try {
      const url = await resolveUrl(this.ctx, this.ref);
      if (!this.isCurrent(epoch)) return;
      // Must precede src so the element is CORS-clean; otherwise createMediaElementSource outputs silence.
      this.el.crossOrigin = 'anonymous';
      this.el.src = url;
      this.el.load();
      this.armStallTimer();
    } catch (err) {
      if (!this.isCurrent(epoch)) return;
      const mapped = toMapped(err);
      this.fail(mapped.code, mapped.message);
    }
  }

  private async probe(assetId: string, epoch: number): Promise<void> {
    try {
      const blob = await this.ctx.assets.getBlob(assetId);
      const codec = blob ? await probeVideoCodec(blob) : null;
      if (!this.isCurrent(epoch)) return;
      this.videoCodec = codec;
      if (this.getStatus().state !== 'error') this.checkDecodable();
    } catch (err) {
      this.log('debug', 'could not read the container header', err);
    }
  }

  protected release(): void {
    const el = this.el;
    for (const name of ['loadedmetadata', 'resize', 'emptied'] as const) el.removeEventListener(name, this.syncSize);
    el.removeEventListener('loadeddata', this.onLoaded);
    el.removeEventListener('playing', this.onPlaying);
    el.removeEventListener('seeked', this.onPlaying);
    el.removeEventListener('ended', this.onEnded);
    el.removeEventListener('error', this.onError);
    el.removeEventListener('pause', this.onPause);
    el.removeEventListener('waiting', this.onBuffering);
    el.removeEventListener('stalled', this.onBuffering);
    el.removeEventListener('canplay', this.onFlowing);
    el.removeEventListener('timeupdate', this.checkOutPoint);
    for (const name of MEDIA_STATE_EVENTS) el.removeEventListener(name, this.notify);
    this.frameObserver.stop();
    this.clearStallTimers();
    el.pause();
    el.removeAttribute('src');
    el.load();
    this.node.disconnect();
    this.gain.disconnect();
    this.pull.disconnect();
  }
}

export const mediaDefinition: SourceTypeDefinition<MediaSettings> = {
  kind: 'media',
  displayName: 'Media File',
  caps: { video: true, audio: true, retry: true, controllableMedia: true },
  category: 'media',
  // A copy would run a second decoder and play the clip's sound twice in the mix, out of step.
  duplicate: 'reference',
  defaults: () => ({ ...MEDIA_DEFAULTS }),
  properties: (): PropertyDescriptor[] => [
    { key: 'assetId', label: 'Media file', type: 'asset', accept: 'video/*,audio/*' },
    {
      key: 'url',
      label: 'or URL',
      type: 'text',
      inputType: 'url',
      applyOn: 'commit',
      validate: validateMediaUrl,
      placeholder: 'https://… (must allow cross-origin use)',
      visibleWhen: (s) => !s.assetId,
    },
    { key: 'loop', label: 'Loop', type: 'boolean' },
    { key: 'autoplay', label: 'Play automatically', type: 'boolean' },
    { key: 'restartOnActivate', label: 'Restart when it goes on program', type: 'boolean' },
    {
      key: 'pauseWhenHidden',
      label: 'Pause when not on program',
      type: 'boolean',
      visibleWhen: (s) => !s.restartOnActivate,
    },
    { key: 'startMs', label: 'Start at', type: 'duration', min: 0, max: 86_400_000, section: 'Trim' },
    { key: 'endMs', label: 'End at (0 = end of file)', type: 'duration', min: 0, max: 86_400_000, section: 'Trim' },
    { key: 'playbackRate', label: 'Speed', type: 'number', min: 0.0625, max: 16, step: 0.05, unit: '×' },
    // Level and mute belong to the mixer strip. These clip-gain settings stay editable only when an
    // older project set them, so they can be put back to neutral.
    {
      key: 'volume',
      label: 'Clip gain (use the mixer for level)',
      type: 'number',
      min: 0,
      max: 2,
      step: 0.05,
      slider: true,
      visibleWhen: (s) => s.volume !== undefined && s.volume !== 1,
    },
    { key: 'muted', label: 'Clip muted (use the mixer to mute)', type: 'boolean', visibleWhen: (s) => s.muted === true },
    { key: 'play', label: 'Play', type: 'button', action: 'play', section: 'Transport' },
    { key: 'pause', label: 'Pause', type: 'button', action: 'pause', section: 'Transport' },
    { key: 'restart', label: 'Restart', type: 'button', action: 'restart', section: 'Transport' },
    { key: 'stop', label: 'Stop', type: 'button', action: 'stop', section: 'Transport' },
  ],
  create: (ctx, settings) => new MediaRuntime(ctx, settings),
};
